import {
  type DocumentVersionDetails,
  type WorkflowStepInput,
} from "~/api/types";
import { type UpdateDocumentVersionData } from "~/api/v2/requests";
import { toISODateString, toISODateTimeString } from "~/helpers/date";
import { z } from "zod";
import i18n from "~/i18n";

const todayAtMidnight = new Date().setHours(0, 0, 0, 0);

export const workflowEditorFormSchema = z
  .object({
    reviewFrom: z.date().nullable(),
    reviewTo: z.date().nullable(),
    // We use these for validation of review dates
    actualStartDate: z.date().nullable(),
    actualEndDate: z.date().nullable(),
    description: z.string(),
    steps: z.array(z.custom<WorkflowStepInput>()).refine(
      (steps) => steps.every((step) => step.name.trim().length > 0),
      i18n.t(($) => $.validations.allStepsMustHaveAName),
    ),
  })
  .refine(
    (data) =>
      data.reviewFrom && data.actualStartDate === null
        ? new Date(data.reviewFrom).setHours(0, 0, 0, 0) >= todayAtMidnight
        : true,
    {
      message: i18n.t(($) => $.validations.startDateMustNotBeInPast),
      path: ["reviewFrom"],
    },
  )
  .refine(
    (data) =>
      data.reviewTo && data.actualEndDate === null
        ? new Date(data.reviewTo).setHours(0, 0, 0, 0) >= todayAtMidnight
        : true,
    {
      message: i18n.t(($) => $.validations.endDateMustNotBeInPast),
      path: ["reviewTo"],
    },
  )
  .refine(
    (data) => {
      if (!data.reviewFrom || !data.reviewTo || data.actualStartDate !== null) {
        return true;
      }

      const fromMs = new Date(data.reviewFrom).setHours(0, 0, 0, 0);
      const toMs = new Date(data.reviewTo).setHours(0, 0, 0, 0);

      return toMs >= fromMs;
    },
    {
      message: i18n.t(($) => $.validations.endDateAfter),
      path: ["reviewTo"],
    },
  );

export type WorkflowEditorFormValues = z.infer<typeof workflowEditorFormSchema>;

export function mapDocumentVersionToFormValues(
  documentVersion: DocumentVersionDetails | undefined,
): WorkflowEditorFormValues | undefined {
  if (!documentVersion) return undefined;

  return {
    reviewFrom: documentVersion.reviewFrom
      ? new Date(documentVersion.reviewFrom)
      : null,
    reviewTo: documentVersion.reviewTo
      ? new Date(documentVersion.reviewTo)
      : null,
    actualStartDate: documentVersion.stepInstances[0]?.createdAt
      ? new Date(documentVersion.stepInstances[0].createdAt)
      : null,
    actualEndDate: documentVersion.publishedAt
      ? new Date(documentVersion.publishedAt)
      : null,
    description: documentVersion.description ?? "",
    steps: documentVersion.steps.map((step) => ({
      ...step,
      _key: `${step.id}`,
    })),
  };
}

export function mapFormValuesToUpdateDocumentVersionData(
  values: WorkflowEditorFormValues,
  dirtyFields: Partial<Record<keyof WorkflowEditorFormValues, unknown>>,
): UpdateDocumentVersionData {
  const data: UpdateDocumentVersionData = {};

  if (dirtyFields.reviewFrom) {
    data.reviewFrom = toISODateString(values.reviewFrom);
  }
  if (dirtyFields.reviewTo) {
    data.reviewTo = toISODateString(values.reviewTo);
  }
  if (dirtyFields.description) {
    data.description = values.description;
  }
  if (dirtyFields.steps) {
    data.steps = values.steps.map((step) => ({
      id: step.id,
      name: step.name.trim(),
      description: step.description ?? null,
      type: step.type,
      dueAt: step.dueAt ? toISODateTimeString(step.dueAt) : null,
      users: step.users.map((user) => ({
        userId: user.userId,
        permission: user.permission,
      })),
    }));
  }

  return data;
}
